/* ---------------------------------------------------------------------------
   RUT chileno — puerto EXACTO de `App\Support\Rut` del backend.

   El backend normaliza el RUT ANTES de validar (`prepareForValidation`), así
   que `unique:users,rut` compara siempre contra la forma canónica
   `12345678-9`: sin puntos, guion antes del dígito verificador, `k` minúscula
   y SIN ceros a la izquierda.

   Acá se replica esa normalización por una sola razón: que el formulario
   pueda avisar del dígito verificador equivocado sin gastar un roundtrip, y
   que lo que se envía ya sea la forma canónica. La AUTORIDAD sigue siendo el
   backend (`ValidRut` + `unique`): esta copia adelanta el error, no lo
   reemplaza.

   Ojo con los ceros a la izquierda: se descartan porque un cero multiplicado
   por su peso aporta cero a la suma del módulo 11, así que `1234567-4` y
   `01234567-4` son el MISMO RUT. Si la forma canónica los distinguiera, la
   unicidad del backend los aceptaría como personas distintas.
--------------------------------------------------------------------------- */

const FACTORES = [2, 3, 4, 5, 6, 7];

/**
 * Reduce cualquier formato de entrada (con o sin puntos, con o sin guion, `K`
 * mayúscula o minúscula, con o sin ceros a la izquierda) a `12345678-9`.
 *
 * No valida el dígito verificador — solo reformatea (ver `esRutValido`).
 */
export function normalizarRut(rut: string): string {
  const limpio = rut.replace(/[^0-9kK]/g, "").toLowerCase();
  const cuerpo = limpio.slice(0, -1).replace(/^0+/, "");
  const dv = limpio.slice(-1);

  return `${cuerpo}-${dv}`;
}

/**
 * Dígito verificador módulo 11: multiplicadores 2..7 en ciclo, arrancando
 * desde el dígito más a la derecha del cuerpo. Resto 11 → `0`; resto 10 → `k`.
 */
export function digitoVerificador(cuerpo: string): string {
  let suma = 0;

  [...cuerpo].reverse().forEach((digito, indice) => {
    suma += Number(digito) * FACTORES[indice % FACTORES.length]!;
  });

  const resto = 11 - (suma % 11);

  if (resto === 11) return "0";
  if (resto === 10) return "k";
  return String(resto);
}

/** ¿El RUT (en cualquier formato de entrada) tiene el dígito verificador correcto? */
export function esRutValido(rut: string): boolean {
  const [cuerpo, dv] = normalizarRut(rut).split("-");

  if (!cuerpo || !/^\d+$/.test(cuerpo)) return false;

  return digitoVerificador(cuerpo) === dv;
}

/**
 * Forma de LECTURA (`12.345.678-9`): puntos de miles sobre la forma canónica.
 * Solo para mostrar; lo que viaja al API es `normalizarRut`.
 */
export function formatearRut(rut: string): string {
  const [cuerpo, dv] = normalizarRut(rut).split("-");

  if (!cuerpo) return rut;

  return `${cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}-${dv}`;
}
